import type { ReactiveMap } from "@solid-primitives/map";
import { createContext, useContext } from "solid-js";
import type { SetStoreFunction } from "solid-js/store";
import type { Api } from "@/api";
import type { ChannelsService } from "@/api/services/ChannelsService.ts";
import type { ChannelContextT } from "@/contexts/channel";
import type { Data } from "@/types/chat";
import { useCurrentUser } from "./currentUser";

// TODO: rename to AckContext

export type ReadTrackingContextT = {
	// TODO: remove these
	// only markCategoryRead is being used currently
	markThreadRead: (
		thread_id: string,
		version_id: string,
		also_local: boolean,
		delay?: boolean,
	) => Promise<void>;
	markCategoryRead: (category_id: string) => Promise<void>;
	markChannelRead: (
		channel_id: string,
		version_id: string,
		also_local: boolean,
		delay: boolean,
	) => Promise<void>;

	// TODO: use this everywhere
	ack: (
		channel_id: string,
		message_id: string,
		also_local: boolean,
		delay: boolean,
	) => Promise<void>;
	// TODO: add bulk ack method
};

const ReadTrackingContext = createContext<ReadTrackingContextT>();

export function createReadTrackingProvider(
	_api: Api,
	channels2: ChannelsService,
	channel_contexts: ReactiveMap<string, ChannelContextT>,
	dataUpdate: SetStoreFunction<Data>,
) {
	const getMe = useCurrentUser();
	const pendingAcks = new Map<string, ReturnType<typeof setTimeout>>();

	const markThreadRead = async (
		thread_id: string,
		version_id: string,
		also_local: boolean,
		delay = false,
	) => {
		if (!getMe()) return;

		let _ackGraceTimeout: ReturnType<typeof setTimeout> | undefined;
		let _ackDebounceTimeout: ReturnType<typeof setTimeout> | undefined;

		if (delay) {
			_ackGraceTimeout = setTimeout(() => {
				_ackDebounceTimeout = setTimeout(() => {
					markThreadRead(thread_id, version_id, also_local, false);
				}, 800);
			}, 200);
			return;
		}

		const cc = channel_contexts.get(thread_id);

		if (cc) {
			const [_ch, chUpdate] = cc;
			if (also_local) {
				chUpdate("read_marker_id", version_id);
			}
			await channels2.ack(thread_id, undefined, version_id);
		} else {
			const c = channels2.cache.get(thread_id);
			if (c) {
				if (also_local) {
					dataUpdate(
						"channels",
						thread_id,
						"read_marker_id",
						c.last_version_id!,
					);
				}
				await channels2.ack(thread_id, undefined, c.last_version_id!);
			}
		}
	};

	const markCategoryRead = async (category_id: string) => {
		if (!getMe()) return;

		const category = channels2.cache.get(category_id);
		if (!category || category.type !== "Category") {
			console.warn("not a category");
			return;
		}

		const childChannels = Array.from(channels2.cache.values()).filter(
			(c) => c.parent_id === category_id && c.room_id === category.room_id,
		);

		const acks = childChannels
			.map((c) => {
				const version_id = c.last_version_id;
				if (!version_id) return null;
				return { channel_id: c.id, version_id };
			})
			.filter((ack): ack is NonNullable<typeof ack> => ack !== null);

		await channels2.ackBulk(acks);

		for (const child of childChannels) {
			if (child.last_version_id) {
				const cc = channel_contexts.get(child.id);
				if (cc) {
					const [_ch, chUpdate] = cc;
					chUpdate("read_marker_id", child.last_version_id);
				}
			}
		}
	};

	const markChannelRead = async (
		channel_id: string,
		version_id: string,
		also_local: boolean,
		delay: boolean,
	) => {
		if (!getMe()) return;

		if (delay) {
			setTimeout(() => {
				markChannelRead(channel_id, version_id, also_local, false);
			}, 300);
			return;
		}

		const cc = channel_contexts.get(channel_id);
		if (cc) {
			const [_ch, chUpdate] = cc;
			if (also_local) {
				chUpdate("read_marker_id", version_id);
			}
			await channels2.ack(channel_id, undefined, version_id);
		} else {
			const c = channels2.cache.get(channel_id);
			if (c) {
				if (also_local) {
					dataUpdate(
						"channels",
						channel_id,
						"read_marker_id",
						c.last_version_id!,
					);
				}
				await channels2.ack(channel_id, undefined, c.last_version_id!);
			}
		}
	};

	const ack = async (
		channel_id: string,
		message_id: string,
		also_local: boolean,
		delay: boolean,
	) => {
		if (!getMe()) return;

		if (delay) {
			const existing = pendingAcks.get(channel_id);
			if (existing) clearTimeout(existing);

			pendingAcks.set(
				channel_id,
				setTimeout(() => {
					pendingAcks.delete(channel_id);
					ack(channel_id, message_id, also_local, false);
				}, 300),
			);
			return;
		} else {
			const existing = pendingAcks.get(channel_id);
			if (existing) {
				clearTimeout(existing);
				pendingAcks.delete(channel_id);
			}
		}

		const timelineState = channel_contexts.get(channel_id)?.[0].timelineState;
		const previous_read_id = timelineState?.readMarkerId;

		// optimistically update, rollback on failure
		if (also_local && timelineState) {
			timelineState.controller.ackMessage(message_id);
		}

		try {
			await channels2.ack(channel_id, message_id, undefined);
		} catch (e) {
			if (also_local && timelineState && previous_read_id) {
				timelineState.controller.ackMessage(previous_read_id);
			}
			throw e;
		}
	};

	return {
		markThreadRead,
		markCategoryRead,
		markChannelRead,
		ack,
	};
}

export const ReadTrackingProvider = (props: {
	api: Api;
	channels2: ChannelsService;
	channel_contexts: ReactiveMap<string, ChannelContextT>;
	dataUpdate: SetStoreFunction<Data>;
	children: import("solid-js").JSX.Element;
}) => {
	const value = createReadTrackingProvider(
		props.api,
		props.channels2,
		props.channel_contexts,
		props.dataUpdate,
	);

	return (
		<ReadTrackingContext.Provider value={value}>
			{props.children}
		</ReadTrackingContext.Provider>
	);
};

export const useReadTracking = () => {
	const context = useContext(ReadTrackingContext);
	if (!context) {
		throw new Error(
			"useReadTracking must be used within a ReadTrackingProvider",
		);
	}
	return context;
};
